import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiAward, FiCheckCircle, FiClock, FiAlertTriangle } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import useReportData from "../../useReportData";

const VendorPerformanceScorecardReport = () => {
  const gridRef = useRef(null);
  const [data] = useReportData("VendorPerformance");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalVendors = data.length;
    const preferredCount = data.filter((r) => r.vendorStatus === "Preferred").length;
    const avgOnTime = (
      data.reduce((s, r) => s + (r.onTimeDeliveryRate || 0), 0) / (totalVendors || 1)
    ).toFixed(1);
    const avgRejection = (
      data.reduce((s, r) => s + (r.rejectionRate || 0), 0) / (totalVendors || 1)
    ).toFixed(1);

    return {
      totalVendors,
      preferredCount,
      avgOnTime,
      avgRejection,
    };
  }, [data]);

  const uniqueCategories = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.primaryCategory).filter(Boolean)));
  }, [data]);

  const getVendorStatusChipType = (status) => {
    if (status === "Preferred") {
      return "success";
    }
    if (status === "Active") {
      return "info";
    }
    if (status === "Under Review") {
      return "warning";
    }
    if (status === "Delinquent") {
      return "error";
    }
    return "default";
  };

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Vendor Name & Category",
        field: "vendorName",
        colId: "vendorName",
        flex: 1.8,
        minWidth: 200,
        cellRenderer: (p) => (
          <div>
            <span style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</span>
            <div style={{ color: "#64748b", fontSize: "11px" }}>
              Primary: {p.data?.primaryCategory}
            </div>
          </div>
        ),
      },
      {
        headerName: "Total POs Issued",
        field: "totalPOsIssued",
        colId: "totalPOsIssued",
        flex: 1.2,
        minWidth: 130,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0b6462" }}>{p.value} Orders</span>
        ),
      },
      {
        headerName: "On-Time Delivery Rate (%)",
        field: "onTimeDeliveryRate",
        colId: "onTimeDeliveryRate",
        flex: 1.6,
        minWidth: 170,
        cellRenderer: (p) => {
          if (p.value == null) {
            return <span style={{ color: "#94a3b8" }}>No deliveries yet</span>;
          }
          const val = p.value;
          const isHigh = val >= 90;
          const isMedium = val >= 80 && val < 90;
          return (
            <div className="d-flex align-items-center" style={{ gap: "8px", width: "100%" }}>
              <div
                style={{
                  flexGrow: 1,
                  background: "#e2e8f0",
                  height: "7px",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${Math.min(val, 100)}%`,
                    background: isHigh ? "#16a34a" : isMedium ? "#d97706" : "#dc2626",
                    height: "100%",
                  }}
                />
              </div>
              <span
                style={{
                  fontWeight: 700,
                  color: isHigh ? "#15803d" : isMedium ? "#b45309" : "#b91c1c",
                  fontSize: "12px",
                  minWidth: "45px",
                }}
              >
                {val}%
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Rejection / Shortage Rate (%)",
        field: "rejectionRate",
        colId: "rejectionRate",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => {
          const val = p.value || 0;
          const isGood = val < 1.5;
          const isWarning = val >= 1.5 && val <= 3.0;
          return (
            <span
              style={{
                fontWeight: 700,
                color: isGood ? "#16a34a" : isWarning ? "#d97706" : "#dc2626",
                background: isGood ? "#f0fdf4" : isWarning ? "#fef3c7" : "#fef2f2",
                padding: "2px 8px",
                borderRadius: "10px",
                fontSize: "12px",
              }}
            >
              {val}%
            </span>
          );
        },
      },
      {
        headerName: "Avg Lead Time (Days)",
        field: "avgLeadTimeDays",
        colId: "avgLeadTimeDays",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "#334155" }}>
            {p.value == null ? "—" : `${p.value} Days`}
          </span>
        ),
      },
      {
        headerName: "Vendor Status",
        field: "vendorStatus",
        colId: "vendorStatus",
        flex: 1.4,
        minWidth: 150,
        cellRenderer: (p) => (
          <StatusChip status={p.value} type={getVendorStatusChipType(p.value)} />
        ),
      },
    ];
  }, []);

  // Filter logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matches =
          (row.vendorName && row.vendorName.toLowerCase().includes(q)) ||
          (row.primaryCategory && row.primaryCategory.toLowerCase().includes(q)) ||
          (row.vendorStatus && row.vendorStatus.toLowerCase().includes(q));
        if (!matches) {
          return false;
        }
      }

      if (statusFilter !== "all" && row.vendorStatus !== statusFilter) {
        return false;
      }

      if (categoryFilter !== "all" && row.primaryCategory !== categoryFilter) {
        return false;
      }

      return true;
    });
  }, [data, searchTerm, statusFilter, categoryFilter]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
    }),
    [],
  );

  const toggleColumn = useCallback((colId) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      if (gridRef.current?.api) {
        gridRef.current.api.setColumnsVisible([colId], next[colId]);
      }
      return next;
    });
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Vendor_Performance_Scorecard_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("Vendor performance scorecard exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="EVALUATED SUPPLIERS"
            value={`${kpis.totalVendors} Vendors`}
            subtitle="Performance scored across active POs"
            icon={FiAward}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="ON-TIME DELIVERY RATE"
            value={`${kpis.avgOnTime}%`}
            subtitle="Benchmark SLA: 90% compliance"
            icon={FiCheckCircle}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="PREFERRED TIER VENDORS"
            value={`${kpis.preferredCount} Suppliers`}
            subtitle="Highest fulfillment compliance"
            icon={FiClock}
            color="#0284c7"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="SHORTAGE / REJECTION RATE"
            value={`${kpis.avgRejection}%`}
            subtitle="Site inspection variance average"
            icon={FiAlertTriangle}
            color="#d97706"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (Search First, then Date, then other filters) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Vendor Performance"
            >
              Vendor Performance
            </div>
            {/* Search Filter FIRST */}
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search vendor, category..."
            />

            {/* Date Filter SECOND */}
            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            {/* Vendor Status Filter THIRD */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Vendor Status</option>
                  <option value="Preferred">Preferred</option>
                  <option value="Active">Active</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Delinquent">Delinquent</option>
                </select>
                <label>Vendor Status</label>
              </div>
            </div>

            {/* Category Filter */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {uniqueCategories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <label>Category</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              animateRows={true}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorPerformanceScorecardReport;
